import { listAndWordFor } from '../../../i18n/types'
import {
  getConsentSubjects,
  hasSiblingSubject,
  isBooleanAnswer,
  scopedStateKey,
  signedSubjects,
  withSiblingAnswers,
  type ConsentSubject,
  type SubjectRow,
} from '../consentSubjects'
import type { ConsentLanguage } from '../hooks/useConsentLanguage'
import type {
  ConsentConfigItem,
  ConsentConfigResponse,
  ConsentContext,
  ConsentDocumentContent,
  SecondaryConsentStateResponse,
  SecondaryElection,
} from './types'

export function interpolate(
  template: string,
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  return template.replace(/\{patient_first_name\}/g, firstName ?? '').replace(/\{patient_last_name\}/g, lastName ?? '')
}

/** Backend requires sectionTitle; JSON.stringify drops undefined keys - avoid silent 422s if config is partial. */
const DEFAULT_SECONDARY_SECTION_TITLE = 'Secondary Consents'

export function resolveSecondarySectionTitle(sectionTitle: string | undefined): string {
  const trimmed = sectionTitle?.trim()
  return trimmed ? trimmed : DEFAULT_SECONDARY_SECTION_TITLE
}

export function normalizePath(pathname: string) {
  return pathname.replace(/\/+$/, '') || '/'
}

export function formatSignedAt(value: string | null | undefined): string | null {
  if (!value) return null
  try {
    return new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return value
  }
}

export function getSecondaryItems(config: ConsentConfigResponse) {
  return config.secondaryItems
}

export function getSecondaryTitle(item: ConsentConfigItem): string {
  return item.title
}

type VariantScenarioKey = 'comparator:default' | 'guardian:proband_only' | 'guardian:duo' | 'guardian:trio'

const titleMap: Record<VariantScenarioKey, (lang: ConsentLanguage, ctx: ConsentContext) => string> = {
  'comparator:default': (lang) => lang.comparatorTitle ?? lang.primaryGuardianCheckboxText,
  'guardian:proband_only': (lang, ctx) =>
    interpolate(
      lang.guardianProbandOnlyTitle ?? lang.primaryGuardianCheckboxText,
      ctx.patientFirstName,
      ctx.patientLastName
    ),
  'guardian:duo': (lang, ctx) =>
    interpolate(
      lang.guardianDuoTrioTitle ?? lang.primaryGuardianCheckboxText,
      ctx.patientFirstName,
      ctx.patientLastName
    ),
  'guardian:trio': (lang, ctx) =>
    interpolate(
      lang.guardianDuoTrioTitle ?? lang.primaryGuardianCheckboxText,
      ctx.patientFirstName,
      ctx.patientLastName
    ),
}

function baseLanguage(locale: string): string {
  return locale.split('-')[0].toLowerCase()
}

function joinSampleLabels(labels: string[], locale: string): string {
  const formatter = new Intl.ListFormat(locale, { type: 'conjunction' })
  const andWord = listAndWordFor(baseLanguage(locale))
  const browserKnowsLanguage = baseLanguage(formatter.resolvedOptions().locale) === baseLanguage(locale)
  if (!andWord || browserKnowsLanguage || labels.length < 2) return formatter.format(labels)
  return `${labels.slice(0, -1).join(', ')} ${andWord} ${labels[labels.length - 1]}`
}

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (placeholder, key: string) => values[key] ?? placeholder)
}

/**
 * Checkbox text naming every subject the guardian signs for, in server order; null when the
 * copy cannot name them all, so the sign screen never shows a sentence that leaves one out.
 */
function getMultiSubjectCheckboxText(
  subjects: ConsentSubject[],
  language: ConsentLanguage,
  locale: string
): string | null {
  const includesSelf = subjects.some((subject) => subject.kind === 'self')
  const template = includesSelf ? language.guardianSiblingsAndSelfTitle : language.guardianSiblingsTitle
  const { subjectSampleLabel, ownSampleLabel } = language
  if (!template?.includes('{samples}') || !subjectSampleLabel || (includesSelf && !ownSampleLabel)) return null
  const labels = subjects.map((subject) =>
    subject.kind === 'self' && ownSampleLabel
      ? ownSampleLabel
      : fill(subjectSampleLabel, { first_name: subject.firstName ?? '', last_name: subject.lastName ?? '' }).trim()
  )
  return fill(template, { samples: joinSampleLabels(labels, language.multiSubjectLocale ?? locale) })
}

export function getPrimaryCheckboxText(
  context: ConsentContext | null,
  language: ConsentLanguage,
  locale = 'en'
): string | null {
  if (!context) return language.primaryGuardianCheckboxText

  const signed = signedSubjects(getConsentSubjects(context))
  if (hasSiblingSubject(signed)) return getMultiSubjectCheckboxText(signed, language, locale)

  const variant = context.primaryConsentCheckboxVariant
  const scenario = context.primaryConsentScenario

  let key: VariantScenarioKey | undefined

  if (variant === 'comparator') {
    key = 'comparator:default'
  } else if (variant === 'guardian' && scenario === 'proband_only') {
    key = 'guardian:proband_only'
  } else if (variant === 'guardian' && scenario === 'duo') {
    key = 'guardian:duo'
  } else if (variant === 'guardian' && scenario === 'trio') {
    key = 'guardian:trio'
  }

  if (key) return titleMap[key](language, context)
  return language.primaryGuardianCheckboxText
}

/** Exact text shown on the primary consent screen, for the consent_event ledger (FAM-108 / G3). */
export function buildPrimaryDocumentContent(
  context: ConsentContext | null,
  language: ConsentLanguage,
  locale: string
): ConsentDocumentContent | null {
  const title = getPrimaryCheckboxText(context, language, locale)
  if (title === null) return null
  return { locale, title, paragraphs: language.primaryConsentParagraphs }
}

/** Exact text shown for a secondary consent item, for the consent_event ledger (FAM-108 / G3). */
export function buildSecondaryDocumentContent(item: ConsentConfigItem, locale: string): ConsentDocumentContent {
  return {
    locale,
    title: getSecondaryTitle(item),
    paragraphs: item.descriptionParagraphs?.length ? item.descriptionParagraphs : [item.description || item.title],
  }
}

export function isOutstandingSecondaryElection(election: SecondaryElection | undefined): boolean {
  return election !== 'opt_in' && election !== 'opt_out'
}

export function draftBooleanFromElection(election: SecondaryElection | undefined): boolean | undefined {
  if (election === 'opt_in') return true
  if (election === 'opt_out') return false
  return undefined
}

type SecondaryStateReadOptions = {
  scopedElectionsAuthoritative?: boolean
}

/**
 * Elections win for keys they name. A stored false next to `unset` stays unanswered.
 * Sibling booleans fill only keys the election map does not name.
 * With no election map, boolean maps apply when a Sibling answer map or Sibling subject list is present.
 */
export function answersFromSecondaryState(
  data: SecondaryConsentStateResponse | undefined,
  subjects: readonly ConsentSubject[] | null = null,
  options: SecondaryStateReadOptions = {}
): {
  toggles: Record<string, boolean>
  elections: Record<string, SecondaryElection>
} {
  const elections = data?.itemElections
  const hasScopedElectionContract = data?.itemScopes !== undefined
  const useScopedElections =
    elections !== undefined &&
    (Object.keys(elections).length > 0 || (options.scopedElectionsAuthoritative && hasScopedElectionContract))

  if (useScopedElections) {
    // When elections are present, shared booleans are legacy noise; keep sibling-specific booleans only.
    const toggles = withSiblingAnswers(undefined, data?.subjectItemStates, isBooleanAnswer)
    for (const [key, election] of Object.entries(elections)) {
      const value = draftBooleanFromElection(election)
      if (value !== undefined) {
        toggles[key] = value
      } else {
        delete toggles[key]
      }
    }
    return { toggles, elections }
  }
  if (elections && Object.keys(elections).length === 0) {
    return {
      toggles: withSiblingAnswers(data?.itemStates, data?.subjectItemStates, isBooleanAnswer),
      elections: {},
    }
  }
  const hasSiblingMap = Boolean(data?.subjectItemStates && Object.keys(data.subjectItemStates).length > 0)
  if (hasSiblingMap || (subjects != null && subjects.length > 0)) {
    return {
      toggles: withSiblingAnswers(data?.itemStates, data?.subjectItemStates, isBooleanAnswer),
      elections: {},
    }
  }
  return { toggles: {}, elections: {} }
}

export function standingPersonSelectionKeys(data: SecondaryConsentStateResponse | undefined): ReadonlySet<string> {
  const elections = data?.itemElections ?? {}
  const scopes = data?.itemScopes ?? {}
  return new Set(
    Object.keys(elections).filter(
      (key) => scopes[key] === 'person' && (elections[key] === 'opt_in' || elections[key] === 'opt_out')
    )
  )
}

export type SecondaryElectionEntry = {
  itemId: string
  sectionTitle: string
  isConsented: boolean
  election: SecondaryElection
  subjectScope?: 'SELF' | 'DEPENDENT'
  subjectFamilyMemberId?: string
}

export function buildSecondaryElectionEntries(
  items: Array<Pick<ConsentConfigItem, 'id'>>,
  draft: Record<string, boolean | undefined>,
  sectionTitle: string | undefined,
  dualSecondary: boolean
): SecondaryElectionEntry[] {
  const resolvedTitle = resolveSecondarySectionTitle(sectionTitle)
  const scopes: Array<'SELF' | 'DEPENDENT' | undefined> = dualSecondary ? ['SELF', 'DEPENDENT'] : [undefined]

  return items.flatMap((item) =>
    scopes.map((scope) => {
      const key = scopedStateKey(item.id, scope)
      const election: SecondaryElection = draft[key] === true ? 'opt_in' : draft[key] === false ? 'opt_out' : 'unset'
      return {
        itemId: item.id,
        sectionTitle: resolvedTitle,
        isConsented: election === 'opt_in',
        election,
        ...(scope ? { subjectScope: scope } : {}),
      }
    })
  )
}

export function buildSubjectSecondaryElectionEntries(
  item: Pick<ConsentConfigItem, 'id'>,
  draft: Record<string, boolean | undefined>,
  sectionTitle: string | undefined,
  rows: readonly SubjectRow[]
): SecondaryElectionEntry[] {
  const resolvedTitle = resolveSecondarySectionTitle(sectionTitle)
  return rows.map(({ subject, key }) => {
    const election: SecondaryElection = draft[key] === true ? 'opt_in' : draft[key] === false ? 'opt_out' : 'unset'
    return {
      itemId: item.id,
      sectionTitle: resolvedTitle,
      isConsented: election === 'opt_in',
      election,
      subjectFamilyMemberId: subject.familyMemberId,
    }
  })
}
